import { createContext, useContext, useState, useCallback } from "react";
import { authStorage } from "../services/api";
import { AuthService } from "../services/corpClaimPortalService";

const AuthContext = createContext(null);

/**
 * The whole auth model here is: OTP login → Sanctum token → localStorage.
 * No refresh tokens, no JWT decoding, no auto-expiry monitoring — Sanctum
 * tokens are opaque and long-lived; the axios interceptor in services/api.js
 * already routes a 401 straight to /login, which is all the expiry handling
 * this app needs.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authStorage.getUser());

  const login = useCallback((token, portalUser) => {
    authStorage.setToken(token);
    authStorage.setUser(portalUser);
    setUser(portalUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // Token may already be invalid — clearing locally is what matters.
    }
    authStorage.clear();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: Boolean(user), login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export default AuthContext;
